import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { DatetimeCustomEvent, IonDatetime } from '@ionic/angular/standalone';

import { DateTime, Interval } from 'luxon';

type HighlightItemObj = {
    date: string;
    textColor: string;
    backgroundColor: string;
    borderColor: string;
};

@Component({
    selector: 'lib-datetime-calendar',
    standalone: true,
    imports: [CommonModule, IonDatetime],
    templateUrl: './datetime-calendar.component.html',
    styleUrl: './datetime-calendar.component.css'
})
export class DatetimeCalendarComponent implements AfterViewInit {
    /* eslint-disable */
    @Input() disabled: boolean = false;
    @Input() id: string = 'datetime-calendar';

    @Input() minDate: string | undefined = undefined;
    @Input() maxDate: string | undefined = undefined;
    @Input() dayValues: number | number[] | string | undefined = undefined;
    @Input() yearValues: number | number[] | string | undefined = undefined;

    @Input() value: string | string[] | null | undefined = null;
    @Input() enableMonthChangeEvent: boolean = true;

    @Input() monthCurrent: string = DateTime.now().toISO();
    @Input() monthFormat: string = 'yyyy-LL';

    @Input() center: boolean = false;

    @Input() multiple: boolean = false;
    @Input() enableStartEndDateSelection: boolean = false;

    @Output() onMonthChange = new EventEmitter();
    @Output() onDateChange = new EventEmitter();

    @ViewChild('datetimeCalendar') datetimeCalendar!: IonDatetime;

    highlightedDates: Array<HighlightItemObj> = [];

    highlightStyles = {
        textColor: 'var(--ion-color-dark)',
        backgroundColor: 'rgba(var(--ion-color-orange-rgb), 0.1)',
        borderColor: 'rgba(var(--ion-color-orange-rgb), 0.1)'
    };

    ngAfterViewInit(): void {
        if (!this.monthCurrent) {
            this.monthCurrent = DateTime.now().toISO();
        }
        if (this.enableMonthChangeEvent) {
            this.listenToHeaderMonthChange();
        }
    }

    listenToHeaderMonthChange() {
        setTimeout(() => {
            const calendarHeader = document
                .getElementById('datetime-calendar')
                ?.shadowRoot?.querySelector('.calendar-next-prev')
                ?.querySelectorAll('ion-button');
            if (calendarHeader && calendarHeader.length > 1) {
                calendarHeader[0].addEventListener('click', this.onPrevMonthClick);
                calendarHeader[1].addEventListener('click', this.onNextMonthClick);
            }
        }, 500);
    }

    onPrevMonthClick = () => {
        this.monthCurrent = DateTime.fromISO(this.monthCurrent).minus({ month: 1 }).toFormat(this.monthFormat);
        this.onMonthChange.emit(this.monthCurrent);
    };

    onNextMonthClick = () => {
        this.monthCurrent = DateTime.fromISO(this.monthCurrent).plus({ month: 1 }).toFormat(this.monthFormat);
        this.onMonthChange.emit(this.monthCurrent);
    };

    sortMultipleDates(dates: string[]) {
        return dates.sort((a: string, b: string) => {
            a = a.split('-').join('');
            b = b.split('-').join('');
            return a.localeCompare(b);
        });
    }

    getDateIntervals(selectedDates: string[]): string[] {
        const startDate = selectedDates[0] ? DateTime.fromISO(selectedDates[0]) : null;
        const endDate = selectedDates[1] ? DateTime.fromISO(selectedDates[1]) : null;

        if (!startDate || !endDate) {
            return [];
        }

        return Interval.fromDateTimes(startDate.startOf('day'), endDate.endOf('day'))
            .splitBy({ day: 1 })
            .filter(d => d.start)
            .map(d => d.start?.toFormat('yyyy-MM-dd')) as string[];
    }

    generateHighlightDates(dates: Array<string | undefined>) {
        return dates
            .filter(date => !!date)
            .map(
                date =>
                    date && {
                        date,
                        ...this.highlightStyles
                    }
            ) as Array<HighlightItemObj>;
    }

    setHighlightedDates(selectedDates: string[]) {
        if (selectedDates.length === 2) {
            const intervalDates = this.getDateIntervals(selectedDates);
            this.highlightedDates = this.generateHighlightDates(intervalDates) || [];
        } else {
            this.highlightedDates = [];
        }
    }

    dateChange(event: DatetimeCustomEvent) {
        const value = event.detail.value;
        this.handleDateChangeValues(value);
    }

    handleDateChangeValues(value: string | string[] | null | undefined) {
        const isArray = Array.isArray(value);

        let emitValue = value;

        if (this.enableStartEndDateSelection && isArray) {
            const dates = value.length > 2 ? value.slice(1, 3) : value;
            const sortedDates = this.sortMultipleDates(dates);

            this.value = sortedDates;
            emitValue = sortedDates;

            this.setHighlightedDates(sortedDates);
        } else {
            this.value = value;
            this.setHighlightedDates([]);
        }

        this.onDateChange.emit(emitValue);
    }
}
